import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Film, Check, PenLine, Copy, Layers } from "lucide-react";
import { toast } from "sonner";

const CHECKLIST = [
  "Framing set (rule of thirds, eyes above center)",
  "Mic close, room quiet",
  "Ring / natural light on face",
  "3 takes of the hook",
  "Deliver script with pauses",
  "Record 5–10s b-roll",
];

export default function FilmThis() {
  const [today, setToday] = useState(null);
  const [checked, setChecked] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/api/plans/today").then(({ data }) => setToday(data));
  }, []);

  const post = today?.post;
  const dayName = new Date().toLocaleDateString("en-US", { weekday: "long" });

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const markFilmed = async () => {
    if (!post) return;
    await api.post(`/api/plans/post/${post.id}/filmed`);
    toast.success("Marked filmed — nice work");
    const { data } = await api.get("/api/plans/today");
    setToday(data);
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Today · {dayName}</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Film this.</h1>
        </div>
      </div>

      {!post ? (
        <div className="p-10 text-muted-foreground">
          Nothing scheduled for today. Head to <button onClick={() => nav("/app/plan")} className="underline">Weekly Plan</button> to generate or edit your week.
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 border border-border rounded p-6 bg-card" data-testid="film-post">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="pillar-badge">{post.goal}</span>
              <span className="pillar-badge">{post.platform}</span>
              <span className="pillar-badge">{post.format}</span>
              <span className="pillar-badge">{post.post_time}</span>
              {post.filmed && <span className="pillar-badge text-primary border-primary/40"><Check className="h-3 w-3"/> Filmed</span>}
            </div>

            <div className="mt-6 font-display font-black text-3xl tracking-tighter leading-tight">{post.topic}</div>

            <div className="mt-6 border-l-4 border-primary pl-4">
              <div className="flex items-center justify-between">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Hook</div>
                <Button size="sm" variant="ghost" data-testid="btn-copy-hook" onClick={() => copy(post.hook, "Hook")}>
                  <Copy className="h-3 w-3"/> Copy
                </Button>
              </div>
              <div className="mt-1 font-display font-bold text-2xl leading-tight">“{post.hook}”</div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Script</div>
                <Button size="sm" variant="ghost" data-testid="btn-copy-script" onClick={() => copy(post.script, "Script")}>
                  <Copy className="h-3 w-3"/> Copy
                </Button>
              </div>
              <div className="mt-1 whitespace-pre-wrap leading-relaxed text-[15px]">{post.script}</div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">B-roll</div>
                  <Button size="sm" variant="ghost" data-testid="btn-copy-broll" onClick={() => copy(post.b_roll, "B-roll notes")}><Copy className="h-3 w-3"/> Copy</Button>
                </div>
                <div className="mt-1 text-sm whitespace-pre-wrap">{post.b_roll}</div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Caption</div>
                  <Button size="sm" variant="ghost" data-testid="btn-copy-caption" onClick={() => copy(post.caption, "Caption")}><Copy className="h-3 w-3"/> Copy</Button>
                </div>
                <div className="mt-1 text-sm whitespace-pre-wrap">{post.caption}</div>
              </div>
            </div>

            <div className="mt-6 border-t border-border pt-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">CTA</div>
              <div className="mt-1 font-semibold">{post.cta}</div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button data-testid="btn-open-variants" variant="outline" onClick={() => nav(`/app/variants/${post.id}?topic=${encodeURIComponent(post.topic)}&hook=${encodeURIComponent(post.hook)}&script=${encodeURIComponent(post.script)}`)}>
                <PenLine className="h-4 w-4"/> Hook variants
              </Button>
              <Button data-testid="btn-open-repurpose" variant="outline" onClick={() => nav(`/app/repurpose/${post.id}?topic=${encodeURIComponent(post.topic)}&hook=${encodeURIComponent(post.hook)}&script=${encodeURIComponent(post.script)}&caption=${encodeURIComponent(post.caption)}`)}>
                <Layers className="h-4 w-4"/> Repurpose to 7 formats
              </Button>
              {!post.filmed && (
                <Button data-testid="btn-film-mark" onClick={markFilmed}>
                  <Film className="h-4 w-4"/> Mark as filmed
                </Button>
              )}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 border border-border rounded p-6 bg-card" data-testid="film-checklist">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Production checklist</div>
            <div className="mt-4 space-y-3">
              {CHECKLIST.map((c, i) => (
                <label key={i} className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    data-testid={`checklist-${i}`}
                    checked={!!checked[i]}
                    onCheckedChange={(v) => setChecked({ ...checked, [i]: !!v })}
                    className="mt-0.5"
                  />
                  <span className={`text-sm leading-relaxed ${checked[i] ? "line-through text-muted-foreground" : ""}`}>{c}</span>
                </label>
              ))}
            </div>

            <div className="mt-6 bg-secondary rounded p-4">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Strategic reason</div>
              <div className="mt-1 text-sm leading-relaxed">{post.strategic_reason}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
